export const INVENTORY_LOT_NAME_MAX_LENGTH = 20

export const normalizeInventoryLotName = value => {
  return String(value || '').trim().replace(/\s+/g, ' ').toUpperCase()
}

export const isValidInventoryLotName = value => {
  const name = normalizeInventoryLotName(value)
  return Boolean(name) && name.length <= INVENTORY_LOT_NAME_MAX_LENGTH
}

export const compareInventoryLotNames = (left, right) => {
  return String(left || '').localeCompare(String(right || ''), undefined, {numeric: true, sensitivity: 'base'})
}

export const resolveInventoryLots = value => {
  if (!value || typeof value !== 'object') return []

  const seen = {}
  return Object.keys(value)
    .map(id => {
      const record = value[id] || {}
      return {
        id,
        name: normalizeInventoryLotName(record.name),
        description: String(record.description || '').trim()
      }
    })
    .filter(lot => {
      if (!lot.name || seen[lot.name]) return false
      seen[lot.name] = true
      return true
    })
    .sort((left, right) => compareInventoryLotNames(left.name, right.name))
}

export const inventoryLotNames = lots => {
  return (Array.isArray(lots) ? lots : [])
    .map(lot => normalizeInventoryLotName(lot && typeof lot === 'object' ? lot.name : lot))
    .filter(Boolean)
}

export const isInventoryLot = (value, lots) => {
  const name = normalizeInventoryLotName(value)
  return Boolean(name) && inventoryLotNames(lots).indexOf(name) !== -1
}

const DEFAULT_INVENTORY_DRIVER_NAMES = Object.freeze([
  'compte inventaire',
  'inventory account'
])

export const isDefaultInventoryDriverName = value => {
  const normalizedName = String(value || '').trim().replace(/\s+/g, ' ').toLowerCase()
  return DEFAULT_INVENTORY_DRIVER_NAMES.indexOf(normalizedName) !== -1
}

export const INVENTORY_TERM = Object.freeze({
  LONG_TERM: 'longTerm',
  SHORT_TERM: 'shortTerm'
})

export const INVENTORY_TERMS = Object.freeze([
  INVENTORY_TERM.LONG_TERM,
  INVENTORY_TERM.SHORT_TERM
])

export const isInventoryTerm = value => {
  return INVENTORY_TERMS.indexOf(String(value || '').trim()) !== -1
}

export const INVENTORY_STATE = Object.freeze({
  IN_INVENTORY: 'in_inventory',
  LEFT_AWAITING_BILLING: 'left_awaiting_billing',
  COMPLETE: 'complete'
})

export const INVENTORY_CONFIRMATION_PHOTO_COUNT = 7

const hasTimestamp = value => {
  if (value === null || typeof value === 'undefined' || value === '') return false
  const timestamp = Number(value)
  return isFinite(timestamp) && timestamp > 0
}

const firstImage = photos => {
  if (!Array.isArray(photos)) return ''
  return photos.find(photo => typeof photo === 'string' && photo.trim() !== '') || ''
}

const historyEntries = historySync => {
  if (!historySync || typeof historySync !== 'object') return []

  if (Array.isArray(historySync)) {
    return historySync
      .map((entry, index) => ({
        key: entry && (entry['.key'] || entry.key)
          ? String(entry['.key'] || entry.key)
          : ('000000000000' + index).slice(-12),
        entry
      }))
      .filter(item => item.entry && typeof item.entry === 'object')
  }

  return Object.keys(historySync)
    .filter(key => key.charAt(0) !== '.' && historySync[key] && typeof historySync[key] === 'object')
    .map(key => ({key, entry: historySync[key]}))
}

export const isInventoryOrder = order => {
  if (!order) return false
  if (order.isInventory === true) return true

  // Some archive/search projections may omit custom fields but retain the order ID.
  const orderId = order['.key'] || order.orderNumber || order.orderNb || order.id
  return /^INV-\d+$/.test(String(orderId || ''))
}

export const isRegularOrder = order => Boolean(order) && !isInventoryOrder(order)

export const isActiveInventoryOrder = order => {
  return isInventoryOrder(order) && !hasTimestamp(order.inventoryBilledAt)
}

export const getInventoryState = order => {
  if (!isInventoryOrder(order)) return null
  if (hasTimestamp(order.inventoryBilledAt)) return INVENTORY_STATE.COMPLETE
  if (hasTimestamp(order.dateEntered) && hasTimestamp(order.dateLeft)) {
    return INVENTORY_STATE.LEFT_AWAITING_BILLING
  }
  return INVENTORY_STATE.IN_INVENTORY
}

export const canSendToInventory = order => {
  return isRegularOrder(order) && order.sentToInventory !== true
}

export const canLeaveInventory = order => {
  return isInventoryOrder(order) &&
    hasTimestamp(order.dateEntered) &&
    !hasTimestamp(order.dateLeft) &&
    !hasTimestamp(order.inventoryBilledAt)
}

export const canMarkInventoryLeaving = canLeaveInventory

export const canBillInventory = order => {
  return isInventoryOrder(order) &&
    hasTimestamp(order.dateEntered) &&
    hasTimestamp(order.dateLeft) &&
    !hasTimestamp(order.inventoryBilledAt)
}

export const canMarkInventoryBilled = canBillInventory

export const getLatestHistoryEntryWithKey = historySync => {
  const entries = historyEntries(historySync)
  if (!entries.length) return null

  const firebasePushEntries = entries.filter(item => item.key.charAt(0) === '-')
  const candidates = firebasePushEntries.length ? firebasePushEntries : entries
  candidates.sort((first, second) => first.key.localeCompare(second.key))
  return candidates[candidates.length - 1]
}

export const getLatestHistoryEntry = historySync => {
  const latest = getLatestHistoryEntryWithKey(historySync)
  return latest ? latest.entry : null
}

export const getLatestDropPhotos = orderOrHistory => {
  const historySync = orderOrHistory && Object.prototype.hasOwnProperty.call(orderOrHistory, 'historySync')
    ? orderOrHistory.historySync
    : orderOrHistory
  const latestHistory = getLatestHistoryEntry(historySync)

  return latestHistory && Array.isArray(latestHistory.dropPhotos)
    ? latestHistory.dropPhotos.slice()
    : []
}

export const getValidLatestDropPhotos = orderOrHistory => {
  return getLatestDropPhotos(orderOrHistory)
    .filter(photo => typeof photo === 'string' && photo.trim() !== '')
}

export const getInventoryImage = (order, placeholder = '') => {
  if (!order || typeof order !== 'object') return placeholder

  return firstImage(getLatestDropPhotos(order)) ||
    firstImage(order.photos) ||
    firstImage(order.bodyPhotos) ||
    placeholder
}

export const hasInventoryConfirmationPhotos = order => {
  return getValidLatestDropPhotos(order).length >= INVENTORY_CONFIRMATION_PHOTO_COUNT
}

export const isInventoryArrivalConfirmed = order => {
  return Boolean(order && hasTimestamp(order.inventoryInboundConfirmedAt))
}

export const hasInventoryExitMarker = order => {
  if (!order || typeof order !== 'object') return false
  return Object.keys(order).some(key => key.indexOf('inventoryExit') === 0)
}

export const canConfirmInventoryArrival = order => {
  const status = String((order && order.status) || '').trim().toLowerCase()
  return isInventoryOrder(order) &&
    hasTimestamp(order.dateEntered) &&
    !hasTimestamp(order.dateLeft) &&
    !hasTimestamp(order.inventoryBilledAt) &&
    (status === 'pick' || status === 'transit') &&
    !hasInventoryExitMarker(order) &&
    !isInventoryArrivalConfirmed(order)
}

export const filterRegularOrders = orders => {
  return Array.isArray(orders) ? orders.filter(isRegularOrder) : []
}

export const filterInventoryOrders = orders => {
  return Array.isArray(orders) ? orders.filter(isInventoryOrder) : []
}

export const filterActiveInventoryOrders = orders => {
  return Array.isArray(orders) ? orders.filter(isActiveInventoryOrder) : []
}

export const isInventoryInYard = order => {
  return getInventoryState(order) === INVENTORY_STATE.IN_INVENTORY && hasTimestamp(order && order.dateEntered)
}

export const isInventoryAwaitingBilling = order => {
  return getInventoryState(order) === INVENTORY_STATE.LEFT_AWAITING_BILLING
}
